import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Camera, Pencil, ScanText } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"
import { api, ErrorApi, pedir } from "@/api/cliente"
import { CampoDeFicha } from "@/componentes/personas/CampoDeFicha"
import { CurriculoDePersona } from "@/componentes/personas/CurriculoDePersona"
import { DatosDePersona } from "@/componentes/personas/DatosDePersona"
import { LectorDePersonal } from "@/componentes/personas/LectorDePersonal"
import { NOMBRE_ARCHIVO, type Propuesta } from "@/componentes/personas/lectorPersonal"
import { PerfilDePersona } from "@/componentes/personas/PerfilDePersona"
import type { Curriculo, Persona } from "@/componentes/personas/persona"
import { RetratoPersona } from "@/componentes/personas/RetratoPersona"
import { VinculoDePersona } from "@/componentes/personas/VinculoDePersona"
import { VistaDePersona } from "@/componentes/personas/VistaDePersona"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

export type { Persona }

/**
 * El expediente de una persona.
 *
 * ⚠ **Esto no es la ficha de un usuario.** Una cuenta es una credencial; esto es un
 * expediente. La mayoría de la plantilla no entra al sistema y su ficha existe igual,
 * y quien se va conserva la suya aunque su cuenta se desactive.
 *
 * ⚠ **El tipo decide qué secciones se pintan, pero quien manda es el servidor.** Un
 * empleado no lleva cliente y un contacto de importador no lleva fecha de ingreso:
 * aquí se esconden los campos que no aplican, y si esto se equivocara el servidor
 * responde 409 con los campos señalados. La regla no se reimplementa.
 *
 * ⚠ **Egresar no borra.** Es una acción aparte y con motivo obligatorio: el
 * expediente es el único sitio donde queda qué contratos tuvo alguien y hasta cuándo.
 *
 * Los campos viven en `DatosDePersona`, `PerfilDePersona`, `VinculoDePersona` y
 * `CurriculoDePersona`; el borrador se queda aquí, que es quien lo guarda.
 *
 * ⚠ **«Leer documentos con IA» llena el BORRADOR, no la ficha.** Lo usado se ve en
 * los campos, se corrige y se guarda con «Guardar cambios» como cualquier otra
 * edición; los archivos leídos viajan con `lectura_id` y entran al expediente en
 * la misma transacción. Los contratos NO están dentro: esto es un `<form>` y
 * el diálogo de contrato lleva el suyo. Se pintan al lado, en la pantalla.
 *
 * ⚠ **Se abre FIJA y se edita a propósito** (03/11, pedido del usuario): abrir a alguien
 * enseña su ficha para leerla (`VistaDePersona`) y «Editar ficha» saca los campos, con
 * «Guardar cambios» y «Cancelar». Leer documentos con IA pasa a edición al usar lo leído,
 * porque eso llena el borrador. Sin `persona:administrar` no hay botón: el servidor no
 * aceptaría el cambio.
 */

const TONO_ESTADO = {
  ACTIVO: "exito",
  SUSPENDIDO: "aviso",
  EGRESADO: "neutro",
} as const

export function FichaPersona({
  persona,
  onGuardado,
  onSucio,
  leerArchivos,
  onLeerAtendido,
}: {
  persona: Persona
  onGuardado: () => void
  /** Avisa de si hay cambios sin guardar. Lo usa la firma, que se genera con lo
   *  GUARDADO y necesita poder decirlo cuando el formulario va por delante. */
  onSucio?: (hay: boolean) => void
  /** (23/10) Lo que el checklist laboral pide leer con IA: abre el lector con ello. */
  leerArchivos?: File[] | null
  onLeerAtendido?: () => void
}) {
  const clienteQuery = useQueryClient()
  const [borrador, setBorrador] = useState<Record<string, string>>({})
  const [motivo, setMotivo] = useState("")
  const [egresando, setEgresando] = useState(false)
  const [curriculo, setCurriculo] = useState<Curriculo>({ estudios: null, experiencias: null })
  const [lectura, setLectura] = useState<Propuesta | null>(null)
  const [leyendo, setLeyendo] = useState(false)
  const [iniciales, setIniciales] = useState<File[] | undefined>(undefined)
  const [editando, setEditando] = useState(false)
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "persona:administrar")
  const puedeLeer = tienePermiso(usuario, "persona:leer_ia") && puedeEditar

  const valor = (campo: keyof Persona) => borrador[campo] ?? (persona[campo] as string | null) ?? ""
  const poner = (campo: string, v: string) => setBorrador((b) => ({ ...b, [campo]: v }))

  const guardar = useMutation({
    mutationFn: () => {
      // Sólo lo que se tocó. Mandar el formulario entero dejaría una línea de
      // bitácora con siete campos cada vez que se corrige uno.
      const cambios: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(borrador)) {
        // Un `<select>` siempre da texto. `usuario_id` es un entero en el esquema, y
        // mandarlo como cadena da un 422 sobre un campo que se eligió de una lista.
        if (k === "usuario_id" || k === "cargo_id" || k === "departamento_id")
          cambios[k] = v === "" ? null : Number(v)
        else cambios[k] = v === "" ? null : v
      }
      if (curriculo.estudios !== null) cambios.estudios = curriculo.estudios
      if (curriculo.experiencias !== null) cambios.experiencias = curriculo.experiencias
      if (lectura !== null) cambios.lectura_id = lectura.lectura_id
      return api.patch(`/api/v1/personas/${persona.id}`, cambios)
    },
    onSuccess: () => {
      setBorrador({})
      setCurriculo({ estudios: null, experiencias: null })
      setLectura(null)
      setEditando(false)
      clienteQuery.invalidateQueries({ queryKey: ["persona-documentos", persona.id] })
      onGuardado()
    },
  })

  /** Deja la ficha como estaba guardada y vuelve a la vista fija. */
  function cancelar() {
    setBorrador({})
    setCurriculo({ estudios: null, experiencias: null })
    setLectura(null)
    guardar.reset()
    setEditando(false)
  }

  const egresar = useMutation({
    mutationFn: () => api.post(`/api/v1/personas/${persona.id}/egreso`, { motivo }),
    onSuccess: () => {
      setEgresando(false)
      setMotivo("")
      onGuardado()
    },
  })

  const subirFoto = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData()
      cuerpo.append("archivo", archivo)
      return pedir(`/api/v1/personas/${persona.id}/foto`, { method: "POST", body: cuerpo })
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["persona-foto", persona.id] })
      onGuardado()
    },
  })

  const esEmpleado = persona.tipo !== "CLIENTE"
  const sucio =
    Object.keys(borrador).length > 0 ||
    curriculo.estudios !== null ||
    curriculo.experiencias !== null ||
    lectura !== null

  function usar(p: Propuesta) {
    setBorrador((b) => ({ ...b, ...p.campos }))
    setCurriculo((c) => ({
      estudios: p.estudios ?? c.estudios,
      experiencias: p.experiencias ?? c.experiencias,
    }))
    setLectura(p)
    setLeyendo(false)
    setEditando(true)
  }

  // (23/10) El checklist sube un documento que alimenta la ficha: se abre el lector con él.
  useEffect(() => {
    if (leerArchivos && leerArchivos.length > 0) {
      setIniciales(leerArchivos)
      setLeyendo(true)
      onLeerAtendido?.()
    }
  }, [leerArchivos, onLeerAtendido])

  // ⚠ En un efecto y no en el render: avisar durante el render actualizaría a la
  // pantalla de arriba mientras ésta se pinta, que es un aviso de React y un
  // re-render de más por cada tecla.
  useEffect(() => {
    onSucio?.(sucio)
  }, [sucio, onSucio])
  const error = guardar.error ?? egresar.error ?? subirFoto.error

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    guardar.mutate()
  }

  return (
    <form onSubmit={enviar} aria-label="Ficha de la persona" className="space-y-6">
      <header className="flex items-start gap-4">
        <RetratoPersona persona={persona} />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            {persona.nombres} {persona.apellidos}
          </h2>
          <p className="font-mono text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {persona.tipo_documento}-{persona.documento}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Insignia tono={TONO_ESTADO[persona.estado]}>{persona.estado}</Insignia>
            <Insignia tono="neutro">{esEmpleado ? "Empleado" : "Contacto de cliente"}</Insignia>
          </div>
          {/* La foto se cambia desde aquí. `capture` no se pone: en un escritorio
              abriría un selector de cámara que no existe. */}
          <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs underline-offset-2 hover:underline">
            <Camera size={14} aria-hidden={true} />
            {persona.tiene_foto ? "Cambiar foto" : "Subir foto"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              data-testid="foto-persona"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) subirFoto.mutate(f)
              }}
            />
          </label>
          {puedeLeer && persona.estado !== "EGRESADO" && (
            <button
              type="button"
              onClick={() => setLeyendo(true)}
              className="mt-2 ml-4 inline-flex items-center gap-1.5 text-xs underline-offset-2 hover:underline"
            >
              <ScanText size={14} aria-hidden={true} />
              Leer documentos con IA
            </button>
          )}
        </div>
      </header>

      {/* En portal: no anida formularios con la ficha. */}
      <Modal
        abierto={leyendo}
        titulo="Leer documentos con IA"
        descripcion="Cédula, RIF y síntesis curricular de esta persona."
        ancho="lg"
        onCerrar={() => {
          setLeyendo(false)
          setIniciales(undefined)
        }}
      >
        <LectorDePersonal
          key={iniciales ? "con-archivos" : "vacio"}
          personaId={persona.id}
          actual={valor}
          onUsar={(p) => {
            usar(p)
            setIniciales(undefined)
          }}
          onCerrar={() => {
            setLeyendo(false)
            setIniciales(undefined)
          }}
          iniciales={iniciales}
        />
      </Modal>

      {lectura !== null && (
        <p className="superficie flex flex-wrap items-center gap-2 p-3 text-sm" role="status">
          <ScanText size={16} aria-hidden={true} style={{ color: "var(--primary)" }} />
          Al guardar se adjuntan al expediente:{" "}
          {lectura.archivos.map((a) => NOMBRE_ARCHIVO[a.tipo]).join(", ")}.
          <button type="button" className="text-xs underline" onClick={() => setLectura(null)}>
            No adjuntarlos
          </button>
        </p>
      )}

      {persona.estado === "EGRESADO" && (
        <p className="superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          Egresó el {comoFecha(persona.egreso_en)}: {persona.motivo_egreso}
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
        </p>
      )}

      {editando ? (
        <>
          <DatosDePersona valor={valor} poner={poner} />
          <PerfilDePersona valor={valor} poner={poner} />
          <VinculoDePersona persona={persona} valor={valor} poner={poner} />
          <CurriculoDePersona
            estudios={curriculo.estudios ?? persona.estudios ?? []}
            experiencias={curriculo.experiencias ?? persona.experiencias ?? []}
            onEstudios={(estudios) => setCurriculo((c) => ({ ...c, estudios }))}
            onExperiencias={(experiencias) => setCurriculo((c) => ({ ...c, experiencias }))}
          />

          <section>
            <h3 className="rotulo mb-3">Notas</h3>
            <textarea
              aria-label="Notas"
              rows={3}
              className="campo"
              value={valor("notas")}
              onChange={(e) => poner("notas", e.target.value)}
            />
          </section>
        </>
      ) : (
        <VistaDePersona persona={persona} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        {editando ? (
          <>
            <button
              type="submit"
              disabled={!sucio || guardar.isPending}
              data-testid="guardar-persona"
              className="boton-primario h-9 px-3 text-sm"
            >
              {guardar.isPending ? "Guardando…" : "Guardar cambios"}
            </button>
            <button
              type="button"
              onClick={cancelar}
              disabled={guardar.isPending}
              className="boton-secundario h-9 px-3 text-sm"
            >
              Cancelar
            </button>
          </>
        ) : (
          puedeEditar && (
            <button
              type="button"
              onClick={() => setEditando(true)}
              className="boton-primario inline-flex h-9 items-center gap-1.5 px-3 text-sm"
            >
              <Pencil size={14} aria-hidden={true} /> Editar ficha
            </button>
          )
        )}
        {persona.estado !== "EGRESADO" && !egresando && !editando && (
          <button
            type="button"
            onClick={() => setEgresando(true)}
            className="boton-secundario h-9 px-3 text-sm"
          >
            Registrar egreso
          </button>
        )}
      </div>

      {egresando && (
        <div className="superficie p-3">
          {/* ⚠ El motivo es obligatorio: una baja sin motivo es una fila que dice que
              alguien se fue y no dice nada más. */}
          <CampoDeFicha id="p-motivo" etiqueta="Motivo del egreso">
            <input
              id="p-motivo"
              className="campo"
              required
              minLength={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </CampoDeFicha>
          <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            El expediente NO se borra: sigue consultable, y es donde queda qué contratos tuvo esta
            persona y hasta cuándo.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setEgresando(false)}
              className="boton-secundario h-8 px-3 text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={motivo.trim().length < 3 || egresar.isPending}
              onClick={() => egresar.mutate()}
              data-testid="confirmar-egreso"
              className="boton-primario h-8 px-3 text-xs"
            >
              Registrar egreso
            </button>
          </div>
        </div>
      )}
    </form>
  )
}
